import React, { useState } from "react";
import { Building2 } from "lucide-react";
import clsx from "clsx";
import { roomColor } from "../FilterBar";
import { ROOM_MOVE_MIME } from "./roomDnd";

/**
 * Room-group separator rendered between block groups in "by-room" view mode.
 * Sister of DateBand. Slightly heavier visual weight than the date sub-bands
 * that appear inside each room group.
 *
 * Doubles as a drop target for the "move row to this room" interaction:
 * Rows + FloatingTaskRows in ROOMS view emit a `application/x-ps-room-move`
 * payload on drag-start; landing it on this band fires `onRoomDrop(roomId, payload)`.
 */
export default function RoomBand({ room, colSpan, taskCount, scheduledCount, floatingCount, onRoomDrop }) {
  const color = roomColor(room?.color_token);
  // If split counts provided, show them; else fall back to the legacy total.
  const showSplit = typeof scheduledCount === "number" && typeof floatingCount === "number";
  const [hover, setHover] = useState(false);

  const onDragOver = (e) => {
    if (!onRoomDrop) return;
    if (!Array.from(e.dataTransfer.types).includes(ROOM_MOVE_MIME)) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    setHover(true);
  };
  const onDragLeave = (e) => {
    // Only clear when the cursor leaves the band element, not when it crosses
    // an inner child.
    if (e.currentTarget === e.target) setHover(false);
  };
  const onDrop = (e) => {
    setHover(false);
    if (!onRoomDrop) return;
    const raw = e.dataTransfer.getData(ROOM_MOVE_MIME);
    if (!raw) return;
    e.preventDefault();
    e.stopPropagation();
    try {
      const payload = JSON.parse(raw);
      if (payload.kind === "room-move") {
        onRoomDrop(room?.id || null, payload);
      }
    } catch (err) {
      console.warn("RoomBand drop payload malformed", err);
    }
  };

  return (
    <div
      style={{ gridColumn: `1 / span ${colSpan}`, height: 28 }}
      className={clsx(
        "border-b border-line-strong border-t-2 flex items-center px-3 mono text-2xs uppercase tracking-[0.2em] sticky left-0 bg-bg-elevated transition-colors",
        hover && "outline outline-2 outline-amber outline-offset-[-2px] bg-amber-glow/20"
      )}
      data-testid={`room-band-${room?.id || "unassigned"}`}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
    >
      <span className="w-2 h-4 mr-2 shrink-0" style={{ background: color }} />
      <Building2 className="w-3 h-3 text-amber mr-2 shrink-0" />
      <span className="text-amber">
        {room ? `${room.abbreviation || ""} · ${room.name}` : "UNASSIGNED ROOM"}
      </span>
      {showSplit ? (
        <span className="ml-3 text-ink-mute normal-case tracking-widest flex items-center gap-2">
          <span data-testid={`room-band-${room?.id || "unassigned"}-scheduled`}>
            {scheduledCount} scheduled
          </span>
          <span className="opacity-40">·</span>
          <span className="text-amber/80" data-testid={`room-band-${room?.id || "unassigned"}-floating`}>
            {floatingCount} unscheduled
          </span>
        </span>
      ) : (
        <span className="ml-3 text-ink-mute normal-case tracking-widest">
          {taskCount} {taskCount === 1 ? "task" : "tasks"}
        </span>
      )}
      {hover && (
        <span className="ml-auto text-amber normal-case tracking-widest">
          drop = move to {room?.abbreviation || "room"} ↩
        </span>
      )}
    </div>
  );
}
